import type { Card, GameState, Move, PileId, Snapshot } from "./types.ts";
import { colorOf, topOf } from "./types.ts";
import {
  applyScoreDelta,
  scoreDeltaForMove,
  scoreDeltaForStockRecycle,
} from "./play-score.ts";

// ===== Validity =====

// A run of cards being moved from a tableau column must be alternating-color descending.
function isValidTableauRun(cards: Card[]): boolean {
  if (cards.length === 0) return false;
  if (!cards[0]!.faceUp) return false;
  for (let i = 0; i < cards.length - 1; i++) {
    const a = cards[i]!;
    const b = cards[i + 1]!;
    if (!a.faceUp || !b.faceUp) return false;
    if (isJoker(a) || isJoker(b)) continue;
    if (a.rank !== b.rank + 1) return false;
    if (colorOf(a.suit) === colorOf(b.suit)) return false;
  }
  return true;
}

export function canPlaceOnTableau(top: Card | undefined, moving: Card): boolean {
  if (isJoker(moving)) return true;
  if (isJoker(top)) return true;
  if (!top) return moving.rank === 13; // empty column accepts only K
  if (!top.faceUp) return false;
  return (
    top.rank === moving.rank + 1 && colorOf(top.suit) !== colorOf(moving.suit)
  );
}

export function canPlaceOnFoundation(
  pile: Card[],
  moving: Card,
): boolean {
  if (isJoker(moving)) return false;
  const top = topOf(pile);
  if (isJoker(top)) return false;
  if (!top) return moving.rank === 1; // foundation starts with A
  return top.suit === moving.suit && top.rank + 1 === moving.rank;
}

function isJoker(card: Card | undefined): boolean {
  return card?.isJoker === true;
}

function getSourcePile(s: GameState, p: PileId): Card[] {
  switch (p.kind) {
    case "tableau":
      return s.tableau[p.index]!;
    case "foundation":
      return s.foundation[p.index]!;
    case "stock":
      return s.stock;
    case "waste":
      return s.waste;
  }
}

// Compute the slice of cards being moved from a source pile.
function takeSlice(s: GameState, m: Move): Card[] | null {
  const src = getSourcePile(s, m.from);
  if (m.count <= 0 || m.count > src.length) return null;

  if (m.from.kind === "tableau") {
    const slice = src.slice(src.length - m.count);
    if (!isValidTableauRun(slice)) return null;
    return slice;
  }

  // Non-tableau sources move exactly 1 card from the top.
  if (m.count !== 1) return null;
  if (m.from.kind === "foundation") {
    return [src[src.length - 1]!];
  }
  if (m.from.kind === "waste") {
    return [src[src.length - 1]!];
  }
  // stock cards are not moved as a Move; use drawFromStock.
  return null;
}

export function canMove(s: GameState, m: Move): boolean {
  const slice = takeSlice(s, m);
  if (!slice) return false;
  const moving = slice[0]!;

  if (m.to.kind === "tableau") {
    const dest = s.tableau[m.to.index];
    if (!dest) return false;
    return canPlaceOnTableau(topOf(dest), moving);
  }
  if (m.to.kind === "foundation") {
    if (slice.length !== 1) return false;
    const dest = s.foundation[m.to.index];
    if (!dest) return false;
    return canPlaceOnFoundation(dest, moving);
  }
  return false;
}

// ===== Mutations =====

export function snapshot(s: GameState): Snapshot {
  return {
    tableau: s.tableau.map((p) => p.map((c) => ({ ...c }))),
    foundation: s.foundation.map((p) => p.map((c) => ({ ...c }))),
    stock: s.stock.map((c) => ({ ...c })),
    waste: s.waste.map((c) => ({ ...c })),
    stockRecycles: s.stockRecycles,
    score: s.score,
    moves: s.moves,
    hintsUsed: s.hintsUsed,
    undosUsed: s.undosUsed,
    joker: { ...normalizeJokerState(s.joker) },
  };
}

function restore(s: GameState, snap: Snapshot): void {
  s.tableau = snap.tableau.map((p) => p.map((c) => ({ ...c })));
  s.foundation = snap.foundation.map((p) => p.map((c) => ({ ...c })));
  s.stock = snap.stock.map((c) => ({ ...c }));
  s.waste = snap.waste.map((c) => ({ ...c }));
  s.stockRecycles = snap.stockRecycles;
  s.score = snap.score;
  s.moves = snap.moves;
  s.hintsUsed = snap.hintsUsed;
  s.undosUsed = snap.undosUsed;
  s.joker = { ...normalizeJokerState(snap.joker) };
}

function normalizeJokerState(
  joker: GameState["joker"] | Snapshot["joker"] | undefined,
): GameState["joker"] {
  return {
    usedThisGame: joker?.usedThisGame ?? false,
    pendingSpend: joker?.pendingSpend ?? false,
  };
}

export function applyMove(s: GameState, m: Move): boolean {
  if (!canMove(s, m)) return false;
  s.undoStack.push(snapshot(s));

  const src = getSourcePile(s, m.from);
  const moving = src.splice(src.length - m.count, m.count);

  if (m.to.kind === "tableau") {
    s.tableau[m.to.index]!.push(...moving);
  } else if (m.to.kind === "foundation") {
    s.foundation[m.to.index]!.push(...moving);
  }

  // Auto-flip top of source tableau when it becomes face-down.
  let revealedTableauCard = false;
  if (m.from.kind === "tableau") {
    const col = s.tableau[m.from.index]!;
    const last = topOf(col);
    if (last && !last.faceUp) {
      last.faceUp = true;
      revealedTableauCard = true;
    }
  }

  s.score = applyScoreDelta(
    s.score,
    scoreDeltaForMove(s.scoringSystem, m, moving, revealedTableauCard),
  );
  s.moves += 1;
  checkWin(s);
  return true;
}

// Draw from stock to waste. In one-mode flips 1 card; in three-mode flips up to 3.
export function drawFromStock(s: GameState): boolean {
  s.undoStack.push(snapshot(s));
  if (s.stock.length === 0) {
    if (s.waste.length === 0) {
      s.undoStack.pop();
      return false;
    }
    // Recycle waste back to stock (face-down).
    while (s.waste.length > 0) {
      const c = s.waste.pop()!;
      c.faceUp = false;
      s.stock.push(c);
    }
    s.stockRecycles = (s.stockRecycles ?? 0) + 1;
    s.score = applyScoreDelta(
      s.score,
      scoreDeltaForStockRecycle(s.scoringSystem, s.drawMode, s.stockRecycles),
    );
    s.moves += 1;
    return true;
  }
  const flipCount = s.drawMode === "three" ? Math.min(3, s.stock.length) : 1;
  for (let i = 0; i < flipCount; i++) {
    const c = s.stock.pop()!;
    c.faceUp = true;
    s.waste.push(c);
  }
  s.moves += 1;
  return true;
}

export function undo(s: GameState): boolean {
  const snap = s.undoStack.pop();
  if (!snap) return false;
  const nextMoves = s.moves + 1;
  const nextUndosUsed = s.undosUsed + 1;
  restore(s, snap);
  s.moves = nextMoves;
  s.undosUsed = nextUndosUsed;
  return true;
}

export function checkWin(s: GameState): void {
  const total = s.foundation.reduce(
    (acc, p) => acc + p.filter((card) => !isJoker(card)).length,
    0,
  );
  if (total === 52) {
    s.finished = true;
    s.won = true;
  }
}

// Autocomplete becomes available the instant all tableau cards are face-up.
// Stock/waste are handled by cycling automatically — no manual draws needed.
export function canAutocomplete(s: GameState): boolean {
  for (const col of s.tableau) {
    for (const c of col) if (!c.faceUp) return false;
  }
  return true;
}

// Find any move that pushes a top card (tableau or waste) onto a foundation.
export function findFoundationPushFromTops(s: GameState): Move | null {
  for (let i = 0; i < s.tableau.length; i++) {
    const col = s.tableau[i]!;
    if (col.length === 0) continue;
    for (let f = 0; f < s.foundation.length; f++) {
      const move: Move = {
        from: { kind: "tableau", index: i },
        to: { kind: "foundation", index: f },
        count: 1,
      };
      if (canMove(s, move)) return move;
    }
  }
  if (s.waste.length > 0) {
    for (let f = 0; f < s.foundation.length; f++) {
      const move: Move = {
        from: { kind: "waste", index: 0 },
        to: { kind: "foundation", index: f },
        count: 1,
      };
      if (canMove(s, move)) return move;
    }
  }
  return null;
}

// Returns true if there is at least one legal non-draw move (foundation push
// or tableau movement). Drawing from stock and recycling are NOT counted —
// callers decide whether those count for their use case.
export function hasAnyPlayMove(s: GameState): boolean {
  if (findFoundationPushFromTops(s)) return true;

  if (s.waste.length > 0) {
    for (let t = 0; t < s.tableau.length; t++) {
      const move: Move = {
        from: { kind: "waste", index: 0 },
        to: { kind: "tableau", index: t },
        count: 1,
      };
      if (canMove(s, move)) return true;
    }
  }

  for (let i = 0; i < s.tableau.length; i++) {
    const col = s.tableau[i]!;
    for (let k = 0; k < col.length; k++) {
      if (!col[k]!.faceUp) continue;
      const count = col.length - k;
      for (let j = 0; j < s.tableau.length; j++) {
        if (j === i) continue;
        const move: Move = {
          from: { kind: "tableau", index: i },
          to: { kind: "tableau", index: j },
          count,
        };
        if (canMove(s, move)) return true;
      }
    }
  }
  return false;
}

function canTableauMoveRevealFaceDown(s: GameState): boolean {
  for (let i = 0; i < s.tableau.length; i++) {
    const col = s.tableau[i]!;
    let firstFaceUp = -1;
    for (let k = 0; k < col.length; k++) {
      if (col[k]!.faceUp) {
        firstFaceUp = k;
        break;
      }
    }
    if (firstFaceUp <= 0) continue;

    const count = col.length - firstFaceUp;
    for (let j = 0; j < s.tableau.length; j++) {
      if (j === i) continue;
      const move: Move = {
        from: { kind: "tableau", index: i },
        to: { kind: "tableau", index: j },
        count,
      };
      if (canMove(s, move)) return true;
    }
  }
  return false;
}

function canMoveWasteTop(s: GameState): boolean {
  if (s.waste.length === 0) return false;
  for (let f = 0; f < s.foundation.length; f++) {
    const move: Move = {
      from: { kind: "waste", index: 0 },
      to: { kind: "foundation", index: f },
      count: 1,
    };
    if (canMove(s, move)) return true;
  }
  for (let t = 0; t < s.tableau.length; t++) {
    const move: Move = {
      from: { kind: "waste", index: 0 },
      to: { kind: "tableau", index: t },
      count: 1,
    };
    if (canMove(s, move)) return true;
  }
  return false;
}

function canMoveWholeColumnOrTopCard(s: GameState): boolean {
  const exposesFoundationPush = (col: Card[], index: number): boolean => {
    if (index !== col.length - 1 || index <= 0) return false;
    const exposed = col[index - 1];
    if (!exposed || !exposed.faceUp) return false;
    for (let f = 0; f < s.foundation.length; f++) {
      if (canPlaceOnFoundation(s.foundation[f]!, exposed)) return true;
    }
    return false;
  };

  for (let i = 0; i < s.tableau.length; i++) {
    const col = s.tableau[i]!;
    for (const index of [0, col.length - 1]) {
      const card = col[index];
      if (!card || !card.faceUp) continue;
      const isWholeColumn = index === 0;
      const isTopCard = index === col.length - 1;
      if (!isWholeColumn && (!isTopCard || !exposesFoundationPush(col, index))) {
        continue;
      }
      const count = col.length - index;
      for (let j = 0; j < s.tableau.length; j++) {
        if (j === i) continue;
        if (s.tableau[j]!.length === 0) continue;
        const move: Move = {
          from: { kind: "tableau", index: i },
          to: { kind: "tableau", index: j },
          count,
        };
        if (canMove(s, move)) return true;
      }
    }
  }
  return false;
}

export function hasHintablePlayMove(s: GameState): boolean {
  return (
    findFoundationPushFromTops(s) != null ||
    canTableauMoveRevealFaceDown(s) ||
    canMoveWasteTop(s) ||
    canMoveWholeColumnOrTopCard(s)
  );
}

// Stuck = no hintable move now, and cycling stock/waste cannot reveal one.
export function isStuck(s: GameState): boolean {
  if (s.finished) return false;
  if (hasHintablePlayMove(s)) return false;
  if (hasUsefulStockCycle(s)) return false;
  return true;
}

function hasUsefulStockCycle(s: GameState): boolean {
  if (s.stock.length === 0 && s.waste.length === 0) return false;
  const sim: GameState = {
    ...s,
    tableau: s.tableau.map((p) => p.map((c) => ({ ...c }))),
    foundation: s.foundation.map((p) => p.map((c) => ({ ...c }))),
    stock: s.stock.map((c) => ({ ...c })),
    waste: s.waste.map((c) => ({ ...c })),
    undoStack: [],
  };
  const maxDraws = (sim.stock.length + sim.waste.length) * 2 + 4;
  for (let i = 0; i < maxDraws; i++) {
    if (!drawFromStock(sim)) return false;
    if (hasHintablePlayMove(sim)) return true;
  }
  return false;
}
